import { useRef } from 'react'
import { useFieldArray, UseFormReturn } from 'react-hook-form'
import { Upload, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { formatFileSize, titleFromFilename } from '../../../documents.utils'
import { DocumentUploadFormData } from '../../../documents-upload.types'

type AddFilesStepProps = {
  form: UseFormReturn<DocumentUploadFormData>
}

export const AddFilesStep = ({ form }: AddFilesStepProps) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const { fields, append, remove } = useFieldArray({ control: form.control, name: 'files' })
  const filesError =
    form.formState.errors.files?.root?.message ?? form.formState.errors.files?.message

  const handleFilesSelected = (fileList: FileList | null) => {
    if (!fileList) return
    Array.from(fileList).forEach((file) => {
      append({ file, title: titleFromFilename(file.name), status: 'idle', errorMessage: null })
    })
    if (inputRef.current) inputRef.current.value = ''
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h3 className="text-lg font-semibold">Add files</h3>
        <p className="text-sm text-muted-foreground">
          Add one or more documents, then set their details next.
        </p>
      </div>

      <label
        className="flex flex-col items-center justify-center gap-2 rounded-md border border-dashed py-10 text-center cursor-pointer hover:bg-slate-50"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault()
          handleFilesSelected(e.dataTransfer.files)
        }}
      >
        <Upload className="h-6 w-6 text-muted-foreground" />
        <span className="text-sm font-medium">Drop files here</span>
        <span className="text-xs text-muted-foreground">No file type or size restrictions</span>
        <Button type="button" variant="outline" size="sm" onClick={() => inputRef.current?.click()}>
          Browse files
        </Button>
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => handleFilesSelected(e.target.files)}
        />
      </label>

      {filesError && <p className="text-sm text-destructive">{filesError}</p>}

      {fields.length > 0 && (
        <ul className="flex flex-col gap-2">
          {fields.map((field, index) => (
            <li
              key={field.id}
              className="flex items-center justify-between rounded-md border px-3 py-2 text-sm"
            >
              <div>
                <p className="font-medium">{field.file.name}</p>
                <p className="text-xs text-muted-foreground">{formatFileSize(field.file.size)}</p>
              </div>
              <Button type="button" variant="ghost" size="icon" onClick={() => remove(index)}>
                <X className="h-4 w-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
